import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
} from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { walletCapture, walletRelease, getTransactionParties, recomputeListingRatings, cancelBooking, rescheduleBooking, requestRescheduleBooking, respondRescheduleRequestBooking, fetchRescheduleCount, rewardLessonComplete, rewardReviewReward, getLearningTutorFormData, submitLearningReport, editLearningReport } from '../../util/api';
import { transitions as bookingTransitions } from '../../transactions/transactionProcessBooking';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { fetchWallet } from '../WalletPage/WalletPage.duck';
import { fetchTrialEligibility } from '../ListingPage/ListingPage.duck';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const SET_PARTY_EMAILS = 'app/TransactionPage/SET_PARTY_EMAILS';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const FETCH_TUTOR_FORM_DATA_REQUEST = 'app/TransactionPage/FETCH_TUTOR_FORM_DATA_REQUEST';
export const FETCH_TUTOR_FORM_DATA_SUCCESS = 'app/TransactionPage/FETCH_TUTOR_FORM_DATA_SUCCESS';
export const FETCH_TUTOR_FORM_DATA_ERROR = 'app/TransactionPage/FETCH_TUTOR_FORM_DATA_ERROR';

export const SUBMIT_LESSON_REPORT_REQUEST = 'app/TransactionPage/SUBMIT_LESSON_REPORT_REQUEST';
export const SUBMIT_LESSON_REPORT_SUCCESS = 'app/TransactionPage/SUBMIT_LESSON_REPORT_SUCCESS';
export const SUBMIT_LESSON_REPORT_ERROR = 'app/TransactionPage/SUBMIT_LESSON_REPORT_ERROR';

export const EDIT_LESSON_REPORT_REQUEST = 'app/TransactionPage/EDIT_LESSON_REPORT_REQUEST';
export const EDIT_LESSON_REPORT_SUCCESS = 'app/TransactionPage/EDIT_LESSON_REPORT_SUCCESS';
export const EDIT_LESSON_REPORT_ERROR = 'app/TransactionPage/EDIT_LESSON_REPORT_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  // Emails fetched via Integration API for calendar invites
  providerEmailIntegration: null,
  customerEmailIntegration: null,
  // Tutor Lesson Report State
  tutorFormData: null,
  fetchTutorFormDataInProgress: false,
  fetchTutorFormDataError: null,
  submitLessonReportInProgress: false,
  submitLessonReportError: null,
  editLessonReportInProgress: false,
  editLessonReportError: null,
  submittedLessonReport: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SET_PARTY_EMAILS:
      return {
        ...state,
        providerEmailIntegration: payload?.providerEmail ?? null,
        customerEmailIntegration: payload?.customerEmail ?? null,
      };

    case FETCH_TUTOR_FORM_DATA_REQUEST:
      return { ...state, fetchTutorFormDataInProgress: true, fetchTutorFormDataError: null };
    case FETCH_TUTOR_FORM_DATA_SUCCESS:
      return { ...state, fetchTutorFormDataInProgress: false, tutorFormData: payload };
    case FETCH_TUTOR_FORM_DATA_ERROR:
      return { ...state, fetchTutorFormDataInProgress: false, fetchTutorFormDataError: payload };

    case SUBMIT_LESSON_REPORT_REQUEST:
      return { ...state, submitLessonReportInProgress: true, submitLessonReportError: null };
    case SUBMIT_LESSON_REPORT_SUCCESS:
      return {
        ...state,
        submitLessonReportInProgress: false,
        submittedLessonReport: payload,
        tutorFormData: state.tutorFormData
          ? { ...state.tutorFormData, existingReport: payload?.report || payload }
          : state.tutorFormData,
      };
    case SUBMIT_LESSON_REPORT_ERROR:
      return { ...state, submitLessonReportInProgress: false, submitLessonReportError: payload };

    case EDIT_LESSON_REPORT_REQUEST:
      return { ...state, editLessonReportInProgress: true, editLessonReportError: null };
    case EDIT_LESSON_REPORT_SUCCESS:
      return {
        ...state,
        editLessonReportInProgress: false,
        submittedLessonReport: payload,
        tutorFormData: state.tutorFormData
          ? { ...state.tutorFormData, existingReport: payload?.report || payload }
          : state.tutorFormData,
      };
    case EDIT_LESSON_REPORT_ERROR:
      return { ...state, editLessonReportInProgress: false, editLessonReportError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = e => ({ type: TRANSITION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const setPartyEmails = ({ providerEmail, customerEmail }) => ({
  type: SET_PARTY_EMAILS,
  payload: { providerEmail, customerEmail },
});

export const fetchMonthlyTimeSlotsRequest = monthId => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = dateId => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  console.log('[duck] fetchTimeSlots called:', {
    listingId: listingId?.uuid || listingId,
    start: start instanceof Date ? start.toISOString() : start,
    end: end instanceof Date ? end.toISOString() : end,
    timeZone,
    useFetchTimeSlotsForDate,
  });

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().TransactionPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      console.log('[duck] fetchTimeSlots returning cached dateData:', dateId);
      return Promise.resolve(dateData?.timeSlots || []);
    }

    dispatch(fetchTimeSlotsForDateRequest(dateId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        console.log('[duck] fetchTimeSlots success (date):', dateId, 'slots:', timeSlots?.length);
        dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        console.error('[duck] fetchTimeSlots error (date):', dateId, e);
        dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
        return [];
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        console.log('[duck] fetchTimeSlots success (month):', monthId, 'slots:', timeSlots?.length);
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        console.error('[duck] fetchTimeSlots error (month):', monthId, e);
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      });
  }
};

// Helper function for loadData call.
export const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing?.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
        ? 'hour'
        : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
          extraQueryParams: {
            intervalDuration: 'P1D',
            intervalAlign,
            maxPerInterval: 1,
            minDurationStartingInInterval,
            perPage: 31,
            page: 1,
          },
        }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

/**
 * Injects a transaction's included provider relationship as the included
 * listing's author relationship.
 * @param {*} txResponse A SDK response from transactions.show().
 * @returns a copy of the txResponse parameter with a listing.author
 * relationship added, if provider exists in the transaction's relationships.
 */
const injectAuthorRelationship = txResponse => {
  const {
    included,
    data: {
      relationships: { provider },
    },
  } = txResponse.data;

  // If provider has not been included, return the response with no changes.
  if (!provider?.data?.id) {
    return txResponse;
  }

  const includedListingIdx = included.findIndex(inc => inc.type === 'listing');

  // We will set the transaction's provider as the listing's author.
  // The full user resource we want to associate with the listing is
  // already available in the response.data.included array, so we only
  // need to add a relationship reference in the included listing resource.
  included[includedListingIdx] = {
    ...included[includedListingIdx],
    relationships: {
      ...included[includedListingIdx].relationships,
      author: {
        data: {
          id: provider?.data?.id,
          type: 'user',
        },
      },
    },
  };

  return {
    ...txResponse,
    data: {
      ...txResponse.data,
      included: [...included],
    },
  };
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  const currentUser = getState().user.currentUser;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;

  const showParams = {
    id,
    include: [
      'customer',
      'customer.profileImage',
      'provider',
      'provider.profileImage',
      'listing',
      'listing.currentStock',
      'listing.images',
      'booking',
      'reviews',
      'reviews.author',
      'reviews.subject',
    ],
    ...getImageVariants(config.layout.listingImage),
  };

  const COMPLETION_TRANSITIONS = [
    'transition/complete',
    'transition/operator-complete',
    'transition/operator-complete-from-meeting',
    'transition/complete-from-interview',
    'transition/mark-received',
    'transition/mark-received-from-purchased',
  ];

  const handleTxResponse = rawResponse => {
    const response = { data: rawResponse };
    const responseData = rawResponse;

    const listingId = listingRelationship(response).id;
    const entities = updatedEntities({}, responseData);
    const listingRef = { id: listingId, type: 'listing' };
    const transactionRef = { id, type: 'transaction' };
    const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
    const listing = denormalised[0];
    const transaction = denormalised[1];
    const processName = resolveLatestProcessName(transaction.attributes.processName);

    try {
      const tx = responseData?.data;
      const providerId = tx?.relationships?.provider?.data?.id?.uuid || tx?.relationships?.provider?.data?.id;
      const customerId = tx?.relationships?.customer?.data?.id?.uuid || tx?.relationships?.customer?.data?.id;
      if (providerId && customerId) {
        getTransactionParties({ providerId, customerId })
          .then(({ providerEmail, customerEmail }) => {
            dispatch(setPartyEmails({ providerEmail, customerEmail }));
          })
          .catch(() => { });
      }
    } catch (_) { }

    // ── On-load reward trigger ──────────────────────────────────────────────
    // Fires whenever the transaction page loads and the session is already
    // in a completed state — covers operator console completions, cron
    // completions, and page refreshes after a UI-triggered completion.
    // The server uses a stable referenceId (lesson_{orderId}) so this is
    // fully idempotent — calling it multiple times never double-credits.
    try {
      const txAttrs = responseData?.data?.attributes || {};
      const lastTransition = txAttrs?.lastTransition;

      if (COMPLETION_TRANSITIONS.includes(lastTransition)) {
        const orderId = responseData?.data?.id?.uuid || responseData?.data?.id;
        const pd = txAttrs?.protectedData || {};
        const meta = txAttrs?.metadata || {};

        const studentId =
          pd.childUserId ||
          responseData?.data?.relationships?.customer?.data?.id?.uuid ||
          null;

        const bookingStart = meta.zoomSyncData?.scheduledStart || pd.sessionTime || pd.sessionStart || null;
        const bookingEnd = meta.zoomSyncData?.scheduledEnd || pd.sessionEnd || null;
        const durationSec = bookingStart && bookingEnd
          ? Math.max(0, Math.floor((new Date(bookingEnd) - new Date(bookingStart)) / 1000))
          : null;

        const isTrial = pd.isTrialBooking === true || meta.bookingType === 'trial';

        if (studentId && orderId) {
          rewardLessonComplete({
            userId: studentId,
            lessonId: orderId,
            completedAt: txAttrs.lastTransitionedAt || new Date().toISOString(),
            durationSec,
            isTrial: isTrial || false,
          }).catch(() => { });
        }
      }
    } catch (_) { }
    // ───────────────────────────────────────────────────────────────────────

    try {
      const process = getProcess(processName);
      const isInquiry = process.getState(transaction) === process.states.INQUIRY;
      const isAcceptedOrScheduled =
        process.getState(transaction) === process.states.ACCEPTED ||
        process.getState(transaction) === process.states.INTERVIEW_SCHEDULED;

      const canFetchTimeslots =
        isBookingProcess(processName) &&
        ((txRole === 'customer' && isInquiry) || isAcceptedOrScheduled);

      if (canFetchTimeslots) {
        fetchMonthlyTimeSlots(dispatch, listing);
      }
    } catch (error) {
      console.log(`transaction process (${processName}) was not recognized`);
    }
    dispatch(fetchTrialEligibility(listingId));

    const injectedResponse = injectAuthorRelationship(response);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    dispatch(addMarketplaceEntities(injectedResponse, sanitizeConfig));
    dispatch(fetchTransactionSuccess(injectedResponse));

    return {
      transaction,
      processName,
      role: txRole,
    };
  };

  return sdk.transactions
    .show(showParams, { expand: true })
    .then(response => {
      return handleTxResponse(response.data);
    })
    .catch(e => {
      if (isFamilyChild && e.status === 403) {
        const { childTransactionShow } = require('../../util/api');
        return childTransactionShow({ childUserId: currentUser.id.uuid, transactionId: id.uuid || id, showParams })
          .then(handleTxResponse);
      }
      if (isFamilyParent && e.status === 403) {
        const { parentTransactionShow } = require('../../util/api');
        return parentTransactionShow({ parentUserId: currentUser.id.uuid, transactionId: id.uuid || id, showParams })
          .then(handleTxResponse);
      }
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;

      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

export const makeTransition = (txId, transitionName, params) => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));

  const isCancelTransition = [
    bookingTransitions.CANCEL,
    'transition/cancel-from-interview',
    'transition/customer-cancel',
    'transition/customer-cancel-from-interview',
    'transition/customer-cancel-late',
    'transition/customer-cancel-late-from-interview',
    'transition/customer-cancel-before-accept',
  ].includes(transitionName);

  if (isCancelTransition) {
    return cancelBooking({ transactionId: txId.uuid || txId, transitionName })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(transitionSuccess());
        dispatch(fetchCurrentUserNotifications());
        dispatch(fetchNextTransitions(txId));
        // Wallet release/capture hooks (non-blocking)
        try {
          const orderId = txId?.uuid || txId;
          const isLateCancel = [
            'transition/customer-cancel-late',
            'transition/customer-cancel-late-from-interview',
          ].includes(transitionName);

          if (isLateCancel) {
            walletCapture({ orderId, reason: 'late-cancel' }).catch(() => { });
          } else {
            walletRelease({ orderId }).catch(() => { });
          }
        } catch (_) { }
        refreshTransactionEntity(sdk, txId, dispatch);
        return response;
      })
      .catch(e => {
        dispatch(transitionError(storableError(e)));
        log.error(e, `${transitionName}-failed`, {
          txId,
          transition: transitionName,
        });
        throw e;
      });
  }

  return sdk.transactions
    .transition({ id: txId, transition: transitionName, params }, { expand: true, include: ["provider", "customer"] })
    .then(response => {
      // Calendar sync moved to TransactionPage UI flow after provider accepts
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());
      dispatch(fetchNextTransitions(txId));

      // Wallet capture/release hooks (non-blocking)
      try {
        const orderId = txId?.uuid || txId;
        const isComplete = [
          bookingTransitions.COMPLETE,
          bookingTransitions.OPERATOR_COMPLETE,
        ].includes(transitionName);

        const isLateCancel = [
          'transition/customer-cancel-late',
          'transition/customer-cancel-late-from-interview',
        ].includes(transitionName);

        const isEarlyCancelOrRelease = [
          bookingTransitions.DECLINE,
          bookingTransitions.OPERATOR_DECLINE,
          bookingTransitions.EXPIRE,
          bookingTransitions.EXPIRE_PAYMENT,
          bookingTransitions.CANCEL,
          'transition/cancel-from-interview',
          'transition/customer-cancel',
          'transition/customer-cancel-from-interview',
          'transition/customer-cancel-before-accept',
        ].includes(transitionName);

        if (isComplete) {
          walletCapture({ orderId, reason: 'complete' }).catch(() => { });

          // ── Trigger lesson-complete reward immediately (fire-and-forget) ──
          // Resolves studentId from the transaction in Redux store
          try {
            const state = getState();
            const transactionRef = state.TransactionPage.transactionRef;
            const entities = state.marketplaceData?.entities;
            const tx = transactionRef && entities
              ? denormalisedEntities(entities, [transactionRef])?.[0]
              : null;

            // Prefer child learner UUID stored at booking time, fall back to customer
            const studentId =
              tx?.attributes?.protectedData?.childUserId ||
              tx?.customer?.id?.uuid ||
              null;

            const bookingStart = tx?.attributes?.protectedData?.sessionTime
              || tx?.attributes?.protectedData?.sessionStart
              || tx?.booking?.attributes?.start
              || null;
            const bookingEnd = tx?.attributes?.protectedData?.sessionEnd
              || tx?.booking?.attributes?.end
              || null;
            const durationSec = bookingStart && bookingEnd
              ? Math.max(0, Math.floor((new Date(bookingEnd) - new Date(bookingStart)) / 1000))
              : null;

            const isTrial =
              tx?.attributes?.protectedData?.isTrialBooking === true ||
              tx?.attributes?.metadata?.bookingType === 'trial';

            if (studentId) {
              rewardLessonComplete({
                userId: studentId,
                lessonId: orderId,
                completedAt: new Date().toISOString(),
                durationSec,
                isTrial: isTrial || false,
              }).catch(() => { });
            }
          } catch (_) { }
        } else if (isLateCancel) {
          walletCapture({ orderId, reason: 'late-cancel' }).catch(() => { });
        } else if (isEarlyCancelOrRelease) {
          walletRelease({ orderId }).catch(() => { });
        }
      } catch (_) { }

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      dispatch(transitionError(storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

export const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  const state = getState();
  const currentUser = state.user.currentUser;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;

  const transactionRef = state.TransactionPage.transactionRef;
  const tx = transactionRef ? denormalisedEntities(state.marketplaceData.entities, [transactionRef])[0] : null;
  const customerId = tx?.customer?.id?.uuid || tx?.relationships?.customer?.data?.id?.uuid;
  const providerId = tx?.provider?.id?.uuid || tx?.relationships?.provider?.data?.id?.uuid;
  const currentUserId = currentUser?.id?.uuid;
  const isParty = currentUserId && (currentUserId === customerId || currentUserId === providerId);

  const queryParams = {
    transaction_id: txId,
    include: ['sender', 'sender.profileImage'],
    ...getImageVariants(config.layout.listingImage),
    ...paging,
  };

  const handleMessagesResponse = response => {
    console.log('[fetchMessages success response]', response);
    const messages = denormalisedResponseEntities(response);
    const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
    const pagination = { totalItems, totalPages, page: fetchedPage };
    const totalMessages = getState().TransactionPage.totalMessages;

    dispatch(fetchMessagesSuccess(messages, pagination));

    if (totalItems > totalMessages && page > 1) {
      dispatch(fetchMessages(txId, 1, config))
        .then(() => {})
        .catch(() => {});
    }
  };

  if (!isParty && (isFamilyParent || isFamilyChild)) {
    const { childMessagesQuery, parentMessagesQuery } = require('../../util/api');
    const proxyCall = isFamilyChild
      ? childMessagesQuery({ childUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, queryParams })
      : parentMessagesQuery({ parentUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, queryParams });

    return proxyCall
      .then(handleMessagesResponse)
      .catch(err => {
        console.error('[fetchMessages proxy call error]', err);
        dispatch(fetchMessagesError(storableError(err)));
        throw err;
      });
  }

  return sdk.messages
    .query(queryParams)
    .then(handleMessagesResponse)
    .catch(e => {
      console.warn('[fetchMessages native error]', e);
      if (isFamilyChild && e.status === 403) {
        const { childMessagesQuery } = require('../../util/api');
        return childMessagesQuery({ childUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, queryParams })
          .then(handleMessagesResponse)
          .catch(err => {
            console.error('[fetchMessages childMessagesQuery error]', err);
            dispatch(fetchMessagesError(storableError(err)));
            throw err;
          });
      }
      if (isFamilyParent && e.status === 403) {
        const { parentMessagesQuery } = require('../../util/api');
        return parentMessagesQuery({ parentUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, queryParams })
          .then(handleMessagesResponse)
          .catch(err => {
            console.error('[fetchMessages parentMessagesQuery error]', err);
            dispatch(fetchMessagesError(storableError(err)));
            throw err;
          });
      }
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  const currentUser = getState().user.currentUser;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch(e => {
      console.warn('[sendMessage native error]', e);
      if (isFamilyChild && e.status === 403) {
        const { childMessageSend } = require('../../util/api');
        return childMessageSend({ childUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, content: message })
          .then(response => {
            console.log('[sendMessage childMessageSend response]', response);
            const messageId = response?.messageId || response?.data?.messageId || 'mock-message-id';
            return dispatch(fetchMessages(txId, 1, config))
              .then(() => {
                dispatch(sendMessageSuccess());
                return messageId;
              })
              .catch(() => dispatch(sendMessageSuccess()));
          })
          .catch(err => {
            console.error('[sendMessage childMessageSend error]', err);
            dispatch(sendMessageError(storableError(err)));
            throw err;
          });
      }
      if (isFamilyParent && e.status === 403) {
        const { parentMessageSend } = require('../../util/api');
        return parentMessageSend({ parentUserId: currentUser.id.uuid, transactionId: txId.uuid || txId, content: message })
          .then(response => {
            console.log('[sendMessage parentMessageSend response]', response);
            const messageId = response?.messageId || response?.data?.messageId || 'mock-message-id';
            return dispatch(fetchMessages(txId, 1, config))
              .then(() => {
                dispatch(sendMessageSuccess());
                return messageId;
              })
              .catch(() => dispatch(sendMessageSuccess()));
          })
          .catch(err => {
            console.error('[sendMessage parentMessageSend error]', err);
            dispatch(sendMessageError(storableError(err)));
            throw err;
          });
      }
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());

  const doSend = hasOtherPartyReviewedFirst
    ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
    : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);

  return doSend.then(res => {
    // Fire-and-forget server recompute of listing rating average (one decimal) and count
    try {
      const rawId =
        tx?.listing?.id?.uuid ||
        tx?.relationships?.listing?.data?.id?.uuid ||
        tx?.relationships?.listing?.data?.id ||
        tx?.listing?.id ||
        null;
      const listingId =
        typeof rawId === 'string'
          ? rawId
          : rawId && typeof rawId.toString === 'function'
            ? rawId.toString()
            : null;
      if (typeof listingId === 'string' && listingId.length > 0) {
        recomputeListingRatings({ listingId }).catch(() => { });
      }
    } catch (_) { }

    // ── Trigger review reward immediately (fire-and-forget) ──
    // Only award to customer (student) — provider reviews don't earn stars
    try {
      const studentId =
        tx?.attributes?.protectedData?.childUserId ||
        tx?.customer?.id?.uuid ||
        null;
      const orderId = tx?.id?.uuid || tx?.id || null;

      if (studentId && orderId) {
        rewardReviewReward({
          userId: studentId,
          lessonId: orderId,
          reviewId: null,
        }).catch(() => { });
      }
    } catch (_) { }

    return res;
  });
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  const state = getState();
  const currentUser = state.user.currentUser;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;

  const transactionRef = state.TransactionPage.transactionRef;
  const tx = transactionRef ? denormalisedEntities(state.marketplaceData.entities, [transactionRef])[0] : null;
  const customerId = tx?.customer?.id?.uuid || tx?.relationships?.customer?.data?.id?.uuid;
  const providerId = tx?.provider?.id?.uuid || tx?.relationships?.provider?.data?.id?.uuid;
  const currentUserId = currentUser?.id?.uuid;
  const isParty = currentUserId && (currentUserId === customerId || currentUserId === providerId);

  if (!isParty && (isFamilyParent || isFamilyChild)) {
    dispatch(fetchTransitionsSuccess([]));
    return Promise.resolve();
  }

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      if ((isFamilyParent || isFamilyChild) && e.status === 403) {
        dispatch(fetchTransitionsSuccess([]));
      } else {
        dispatch(fetchTransitionsError(storableError(e)));
      }
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => (dispatch, getState) => {
  const state = getState();
  const selectedCurrency = orderData?.selectedCurrency || state.currency?.selectedCurrency || 'AED';
  const fxRates = state.currency?.fxRates || { AED: 3.6725, EUR: 0.92, SAR: 3.75, GBP: 0.79, CAD: 1.36, AUD: 1.52 };
  const fxRate = orderData?.fxRate || fxRates[selectedCurrency] || 1.0;

  const enrichedOrderData = {
    ...orderData,
    selectedCurrency,
    fxRate,
  };

  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData: enrichedOrderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
    });
};

export const rescheduleSession = (transactionId, newStartTime) => (dispatch, getState, sdk) => {
  return rescheduleBooking({ transactionId, newStartTime });
};

export const requestLateReschedule = (transactionId, proposedStartTime, reason) => (dispatch, getState, sdk) => {
  return requestRescheduleBooking({ transactionId, proposedStartTime, reason });
};

export const respondRescheduleRequest = (transactionId, action, newStartTime) => (dispatch, getState, sdk) => {
  return respondRescheduleRequestBooking({ transactionId, action, newStartTime });
};

export const fetchStudentRescheduleCount = () => (dispatch, getState, sdk) => {
  return fetchRescheduleCount();
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));
  dispatch(fetchWallet())
  dispatch(fetchCurrentUserNotifications());

  // Sale / order (i.e. transaction entity in API)
  // Fetch transaction details first so subsequent selectors can read current roles.
  return dispatch(fetchTransaction(txId, txRole, config))
    .then(result => {
      const transaction = result?.transaction;
      const learnerId = transaction?.attributes?.protectedData?.learnerId;
      const promises = [
        dispatch(fetchMessages(txId, 1, config)),
        dispatch(fetchNextTransitions(txId)),
      ];
      if (learnerId) {
        const { showUser } = require('../ProfilePage/ProfilePage.duck');
        promises.push(dispatch(showUser(new UUID(learnerId), config)).catch(() => {}));
      }
      return Promise.all(promises);
    });
};

// ================ Tutor Lesson Report Action Creators & Thunks ================ //

export const fetchTutorFormDataRequest = () => ({ type: FETCH_TUTOR_FORM_DATA_REQUEST });
export const fetchTutorFormDataSuccess = payload => ({ type: FETCH_TUTOR_FORM_DATA_SUCCESS, payload });
export const fetchTutorFormDataError = e => ({ type: FETCH_TUTOR_FORM_DATA_ERROR, error: true, payload: e });

export const submitLessonReportRequest = () => ({ type: SUBMIT_LESSON_REPORT_REQUEST });
export const submitLessonReportSuccess = payload => ({ type: SUBMIT_LESSON_REPORT_SUCCESS, payload });
export const submitLessonReportError = e => ({ type: SUBMIT_LESSON_REPORT_ERROR, error: true, payload: e });

export const editLessonReportRequest = () => ({ type: EDIT_LESSON_REPORT_REQUEST });
export const editLessonReportSuccess = payload => ({ type: EDIT_LESSON_REPORT_SUCCESS, payload });
export const editLessonReportError = e => ({ type: EDIT_LESSON_REPORT_ERROR, error: true, payload: e });

export const fetchTutorFormDataThunk = ({ transactionId, parentId, childId }) => dispatch => {
  dispatch(fetchTutorFormDataRequest());
  return getLearningTutorFormData({ transactionId, parentId, childId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(fetchTutorFormDataSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch tutor form data');
    })
    .catch(err => {
      dispatch(fetchTutorFormDataError(err.message));
      throw err;
    });
};

export const submitLessonReportThunk = payload => dispatch => {
  dispatch(submitLessonReportRequest());
  return submitLearningReport(payload)
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(submitLessonReportSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to submit lesson report');
    })
    .catch(err => {
      dispatch(submitLessonReportError(err.message));
      throw err;
    });
};

export const editLessonReportThunk = payload => dispatch => {
  dispatch(editLessonReportRequest());
  return editLearningReport(payload)
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(editLessonReportSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to update lesson report');
    })
    .catch(err => {
      dispatch(editLessonReportError(err.message));
      throw err;
    });
};
